import MessageIcon from '@mui/icons-material/Message'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { KNShareIcon } from 'components/KNIcon/KNMaterialIcon'
import { Role } from 'context/authentication/Role.types'
import { analyticsEvent } from 'global/helpers/analytics'
import useDialog from 'global/hooks/useDialog'
import { hasAccess } from 'global/helpers/authorization'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import DriverAssignment from 'screens/TripDashboard/DriverAssignment'
import ShareDialog from 'screens/TripDashboard/ShareDialog'
import SmsDriverDialog from 'screens/TripDashboard/SmsDriverDialog'
import TripCargo from 'screens/TripDashboard/TripCargo'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import TripHeader from 'screens/TripDashboard/TripHeader'
import VehicleAssignment from 'screens/TripDashboard/VehicleAssignment'
import type { LegData } from './TripDetails.types'

export interface TripSummaryProps {
  trip: TripData
  legs: LegData[]
  weblinkToken?: string
  onChange?: (updatedTrip?: TripData) => void
}

const TripSummary = ({ trip, legs, weblinkToken, onChange }: TripSummaryProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const smsDialog = useDialog<typeof SmsDriverDialog>()
  const shareDialog = useDialog<typeof ShareDialog>()
  const canShareTrip = Boolean(weblinkToken) || hasAccess(Role.Editor)

  const handleShareClick = useCallback((): void => {
    shareDialog.handleOpen()
    analyticsEvent('polestar_cs_share_trip_button_clicked')
  }, [shareDialog.handleOpen])

  return (
    <>
      <Stack spacing={0.5}>
        <TripHeader trip={trip} legs={legs} stacked size="large" weblinkToken={weblinkToken} />
        <TripCargo trip={trip} />
      </Stack>
      {isMobile && trip.carrierInstructions && (
        <Stack>
          <Typography variant="p6" color="primary.main">
            {t('screens.cs.trip_dashboard.trip_details.instructions')}
          </Typography>
          <Typography component="p" variant="p3" color="text.main">
            {trip.carrierInstructions}
          </Typography>
        </Stack>
      )}
      {!weblinkToken && (
        <Grid container spacing={1}>
          <Grid size={{ xs: 6 }}>
            <Stack spacing={0.25}>
              <Typography variant="p6" color="primary.main">
                {t('screens.cs.trip_dashboard.card.columns.vehicle')}
              </Typography>
              <VehicleAssignment trip={trip} onChange={onChange} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Stack spacing={0.25}>
              <Typography variant="p6" color="primary.main">
                {t('screens.cs.trip_dashboard.card.columns.driver')}
              </Typography>
              <DriverAssignment trip={trip} onChange={onChange} />
            </Stack>
          </Grid>
        </Grid>
      )}
      {canShareTrip && (!weblinkToken || !isMobile) && (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="end"
          alignItems="center"
        >
          {trip.assignedDriver?.contactNumber && (
            <Button
              variant="outlined"
              size="small"
              endIcon={<MessageIcon />}
              onClick={smsDialog.handleOpen}
            >
              {t('screens.cs.trip_dashboard.card.actions.sms_driver')}
            </Button>
          )}
          <Button
            data-guide="trip-share"
            variant="outlined"
            size="small"
            endIcon={<KNShareIcon />}
            onClick={handleShareClick}
          >
            {t('screens.cs.trip_dashboard.card.actions.share_trip_short')}
          </Button>
        </Stack>
      )}

      <SmsDriverDialog
        payload={{
          trip,
        }}
        open={smsDialog.isOpen}
        onAction={smsDialog.handleAction}
        onClose={smsDialog.handleClose}
      />
      <ShareDialog
        payload={{
          trip,
          weblinkToken,
        }}
        open={shareDialog.isOpen}
        onAction={shareDialog.handleAction}
        onClose={shareDialog.handleClose}
      />
    </>
  )
}

export default TripSummary
